import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ExportModal from '../common/ExportModal';
import UploadModal from '../common/UploadModal';
import EditableCell from '../common/EditableCell';
import FilterDropdown from '../common/FilterDropdown';
import { EXPORT_CONFIGS } from '../../config/exportConfigs';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';
import useResizableColumns from '../../hooks/useResizableColumns';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import MaterialAnalyticsDashboard from './MaterialAnalyticsDashboard';

// Column definitions for the material log table
const COLUMNS = [
  { key: 'part_number', label: 'Part Number', type: 'text', width: 120 },
  { key: 'mat_mngmnt_type', label: 'Type', type: 'text', width: 100 },
  { key: 'ottav_type', label: 'Ottav Type', type: 'text', width: 100 },
  { key: 'sch', label: 'SCH', type: 'text', width: 60 },
  { key: 'mat', label: 'Material', type: 'text', width: 80 },
  { key: 'size', label: 'Size', type: 'text', width: 80 },
  { key: 'qty_needed_for_job', label: 'Qty Needed', type: 'number', width: 90 },
  { key: 'qty_in_stock', label: 'In Stock', type: 'number', width: 80 },
  { key: 'qty_received', label: 'Received', type: 'number', width: 80 },
  { key: 'received_by', label: 'Received By', type: 'text', width: 100 },
  { key: 'po_number_or_fi', label: 'PO Number/FI', type: 'text', width: 120 },
  { key: 'po_qty', label: 'PO Qty', type: 'number', width: 70 },
  { key: 'backorder', label: 'Backorder', type: 'computed', width: 80 },
  { key: 'packing_slip', label: 'Packing Slip', type: 'text', width: 100 },
  { key: 'priority', label: 'Priority', type: 'select', width: 90, options: ['High', 'Medium', 'Low'] },
  { key: 'comments', label: 'Comments', type: 'text', width: 150 },
  { key: 'Received_%', label: 'Received %', type: 'number', width: 90 },
  { key: 'location', label: 'Location', type: 'text', width: 100 },
  { key: 'po_date', label: 'PO Date', type: 'date', width: 100 },
  { key: 'received_date', label: 'Received Date', type: 'date', width: 110 },
];

// Custom render for priority badges
const renderMaterialValue = (val, column) => {
  if (column.key === 'priority' && val) {
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.8rem',
        backgroundColor: val === 'High' ? '#dc3545' : val === 'Medium' ? '#ffc107' : '#28a745',
        color: 'white'
      }}>
        {val}
      </span>
    );
  }
  return null; // Use default formatting
};

const MaterialLog = ({ projectId, jobNumber }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const downloadedJobsRef = useRef(new Set());

  const fetchMaterialsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchMaterialsRef.current) fetchMaterialsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('materialLog', projectId || jobNumber || 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Use reusable hooks for filtering and sorting
  const filterHook = useTableFilter({ columns: COLUMNS, items: materials });
  const sortHook = useTableSort({ columns: COLUMNS });

  // Resizable columns
  const defaultWidths = useMemo(() => {
    const widths = {};
    COLUMNS.forEach(col => { widths[col.key] = col.width; });
    return widths;
  }, []);
  const { getColumnWidth, getResizeHandleProps } = useResizableColumns(
    `materialLog_${projectId}_columnWidths`,
    defaultWidths
  );

  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/material-management'
      );

      if (response.data.success) {
        setMaterials(response.data.data || []);
      } else {
        setError('Failed to load material log');
      }
    } catch (err) {
      console.error('Error fetching material log:', err);
      setError('Failed to load material log. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchMaterialsRef.current = fetchMaterials; }, [fetchMaterials]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);


  // Export handlers
  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      setShowExportModal(false);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/material/${projectId}/export`, null, syncHeaders());
      if (response.data.success) {
        setExportJobId(response.data.jobId);
        showToast(`Export started! Job ID: ${response.data.jobId}`, 'success');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      setIsExporting(false);
      showToast(`Export error: ${error.message}`, 'error');
    }
  };

  const handleExportJobComplete = (jobData) => {
    showToast(`Export completed! ${jobData.processed_rows} records exported.`, 'success');
    setIsExporting(false);
    setExportJobId(null);
    if (jobData.job_id && !downloadedJobsRef.current.has(jobData.job_id)) {
      downloadedJobsRef.current.add(jobData.job_id);
      window.open(`/api/download/${jobData.job_id}`, '_blank');
    }
  };

  const handleExportJobError = (errorMessage) => {
    showToast(`Export failed: ${errorMessage}`, 'error');
    setIsExporting(false);
    setExportJobId(null);
  };

  const handleSaveCell = async (rowId, columnKey, newValue) => {
    setSavingRows(prev => new Set(prev).add(rowId));

    try {
      // Convert value based on column type
      const column = COLUMNS.find(c => c.key === columnKey);
      let processedValue = newValue;
      
      if (column?.type === 'number') {
        processedValue = newValue === '' ? null : Number(newValue);
      } else if (column?.type === 'date') {
        processedValue = newValue === '' ? null : newValue;
      }

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/material-management/${rowId}`,
        { [columnKey]: processedValue },
        syncHeaders()
      );

      if (response.data.success) {
        // Update local state
        setMaterials(prev => prev.map(m => 
          m.id === rowId ? { ...m, [columnKey]: processedValue } : m
        ));
        showToast('Saved successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving cell:', err);
      showToast(`Save failed: ${err.message}`, 'error');
      throw err;
    } finally {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/material-management',
        newRow,
        syncHeaders()
      );

      if (response.data.success) {
        setMaterials(prev => [response.data.data, ...prev]);
        setNewRow({});
        setIsAddingNew(false);
        showToast('New record added successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to add record');
      }
    } catch (err) {
      console.error('Error adding new record:', err);
      showToast(`Failed to add record: ${err.message}`, 'error');
    }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/material-management/${rowId}`,
        null,
        syncHeaders()
      );

      if (response.data.success) {
        setMaterials(prev => prev.filter(m => m.id !== rowId));
        showToast('Record deleted successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && filterHook.openFilterColumn) {
        filterHook.setOpenFilterColumn(null);
        filterHook.setFilterSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook]);

  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  // Filter and sort materials
  const filteredMaterials = useMemo(() => {
    let result = [...materials];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(m => 
        COLUMNS.some(col => {
          const val = m[col.key];
          return val !== null && val !== undefined && String(val).toLowerCase().includes(search);
        })
      );
    }

    // Apply column filters
    result = filterHook.applyFilters(result);

    // Apply sorting
    result = sortHook.applySorting(result);

    return result;
  }, [materials, searchTerm, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{
          display: 'inline-block',
          width: '48px',
          height: '48px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '1rem', color: '#666' }}>Loading material log...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <strong>⚠️ Error:</strong> {error}
        <button
          onClick={fetchMaterials}
          style={{
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#333' }}>📦 Material Log</h3>
          <span style={{ 
            backgroundColor: '#007bff', 
            color: 'white', 
            padding: '4px 12px', 
            borderRadius: '20px',
            fontSize: '0.85rem'
          }}>
            {filteredMaterials.length} records
          </span>
          
          {/* Active Filters Bar */}
          {hasActiveFilters && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '12px', color: '#065f46', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold' }}>Filters:</span>
              {searchTerm && <span style={{ padding: '2px 6px', backgroundColor: '#10b981', color: 'white', borderRadius: '3px', fontSize: '11px' }}>Search: "{searchTerm}"</span>}
              {filterHook.activeFilters.map((f, idx) => (
                <span key={idx} style={{ padding: '2px 6px', backgroundColor: '#10b981', color: 'white', borderRadius: '3px', fontSize: '11px' }}>
                  {f.label}: {f.type === 'values' ? `${f.count} selected` : f.type === 'text' ? `${f.mode} "${f.value}"` : f.description || f.value}
                </span>
              ))}
              <button onClick={handleClearAll} style={{ padding: '2px 8px', backgroundColor: '#065f46', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>✕ Clear All</button>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px 8px 36px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                width: '200px',
                fontSize: '0.9rem'
              }}
            />
            <span style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d'
            }}>🔍</span>
          </div>

          {/* Analytics Button */}
          <button
            onClick={() => setShowAnalytics(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            📊 Analytics
          </button>

          {/* Export Button */}
          <button
            onClick={() => setShowExportModal(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            📤 Export
          </button>

          {/* Import Button */}
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            📥 Import
          </button>

          {/* Add New Button */}
          <button
            onClick={() => setIsAddingNew(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            ➕ Add New
          </button>

          {/* Refresh Button */}
          <button
            onClick={fetchMaterials}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: '#e7f3ff',
        padding: '0.75rem 1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        fontSize: '0.85rem',
        color: '#0056b3'
      }}>
        💡 <strong>Tip:</strong> Click any cell to edit. Changes are saved automatically.
      </div>

      {/* Add New Row Form */}
      {isAddingNew && (
        <div style={{
          backgroundColor: '#fff3cd',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #ffc107'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#856404' }}>Add New Material</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
            {COLUMNS.filter(c => c.type !== 'computed').slice(0, 10).map(col => (
              <div key={col.key}>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                  {col.label}
                </label>
                {col.type === 'select' ? (
                  <select
                    value={newRow[col.key] || ''}
                    onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="">--</option>
                    {col.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    type={col.type === 'date' ? 'date' : col.type === 'number' ? 'number' : 'text'}
                    value={newRow[col.key] || ''}
                    onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleAddNewRow}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ✓ Save
            </button>
            <button
              onClick={() => { setIsAddingNew(false); setNewRow({}); }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        maxHeight: 'calc(100vh - 350px)',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: 'white'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.85rem',
          minWidth: '1800px'
        }}>
          <thead style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#343a40',
            color: 'white',
            zIndex: 10
          }}>
            <tr>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'center',
                width: '60px',
                borderRight: '1px solid #495057',
                position: 'sticky',
                left: 0,
                backgroundColor: '#343a40',
                zIndex: 11
              }}>
                Actions
              </th>
              {COLUMNS.map(col => {
                const colWidth = getColumnWidth(col.key, col.width);
                return (
                <th key={col.key} style={{ 
                  padding: '8px 4px', 
                  textAlign: col.type === 'number' ? 'right' : 'left',
                  width: colWidth,
                  minWidth: colWidth,
                  maxWidth: colWidth,
                  borderRight: '1px solid #495057',
                  whiteSpace: 'nowrap',
                  position: 'relative',
                  userSelect: 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start', gap: '4px' }}>
                    <span 
                      onClick={() => sortHook.handleSort(col.key)}
                      style={{ cursor: 'pointer', flex: 1 }}
                      title="Click to sort"
                    >
                      {col.label}
                      {sortHook.sortColumn === col.key && (
                        <span style={{ marginLeft: '4px' }}>
                          {sortHook.sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </span>
                    {col.type !== 'computed' && (
                      <span
                        onClick={(e) => { e.stopPropagation(); filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); }}
                        style={{ 
                          cursor: 'pointer', 
                          opacity: filterHook.hasColumnFilter(col.key) ? 1 : 0.5,
                          fontSize: '0.75rem',
                          color: filterHook.hasColumnFilter(col.key) ? '#10b981' : 'white'
                        }}
                        title="Filter"
                      >
                        🔽
                      </span>
                    )}
                  </div>
                  {/* Filter Dropdown */}
                  {filterHook.openFilterColumn === col.key && (
                    <FilterDropdown
                      column={col}
                      uniqueValues={filterHook.getUniqueValues(col.key)}
                      selectedValues={filterHook.selectedValues[col.key] || []}
                      textFilter={filterHook.textFilters[col.key] || null}
                      numericFilter={filterHook.numericFilters[col.key] || null}
                      dateFilter={filterHook.dateFilters[col.key] || null}
                      filterSearch={filterHook.filterSearch}
                      onToggleValue={filterHook.toggleValue}
                      onSelectAll={filterHook.selectAll}
                      onClearColumn={filterHook.clearColumn}
                      onApplyTextFilter={filterHook.applyTextFilter}
                      onApplyNumericFilter={filterHook.applyNumericFilter}
                      onApplyDateFilter={filterHook.applyDateFilter}
                      onClose={() => {
                        filterHook.setOpenFilterColumn(null);
                        filterHook.setFilterSearch('');
                      }}
                      onSearchChange={filterHook.setFilterSearch}
                    />
                  )}
                  <div {...getResizeHandleProps(col.key, colWidth)} />
                </th>
              );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredMaterials.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} style={{ 
                  padding: '3rem', 
                  textAlign: 'center', 
                  color: '#6c757d' 
                }}>
                  {searchTerm ? 'No matching records found' : 'No material records. Click "Add New" to create one.'}
                </td>
              </tr>
            ) : (
              filteredMaterials.map((material, index) => (
                <tr 
                  key={material.id} 
                  style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    borderBottom: '1px solid #dee2e6'
                  }}
                >
                  <td style={{ 
                    padding: '4px 8px', 
                    textAlign: 'center',
                    borderRight: '1px solid #eee',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    zIndex: 5
                  }}>
                    <button
                      onClick={() => handleDeleteRow(material.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title="Delete row"
                    >
                      🗑️
                    </button>
                  </td>
                  {COLUMNS.map(col => {
                    const colWidth = getColumnWidth(col.key, col.width);
                    return (
                    <td 
                      key={col.key} 
                      style={{ 
                        padding: '4px 8px',
                        textAlign: col.type === 'number' ? 'right' : 'left',
                        borderRight: '1px solid #eee',
                        backgroundColor: savingRows.has(material.id) ? '#fff3cd' : 'inherit',
                        width: colWidth,
                        minWidth: colWidth,
                        maxWidth: colWidth
                      }}
                    >
                      <EditableCell
                        value={material[col.key]}
                        row={material}
                        column={col}
                        onSave={handleSaveCell}
                        disabled={col.type === 'computed'}
                        renderValue={renderMaterialValue}
                      />
                    </td>
                  );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Export Job Monitor */}
      {exportJobId && (
        <JobStatusMonitor
          jobId={exportJobId}
          onComplete={handleExportJobComplete}
          onError={handleExportJobError}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          {...EXPORT_CONFIGS.materialLog}
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          jobNumber={jobNumber}
        />
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          fetchMaterials();
          setShowUploadModal(false);
        }}
        onUploadError={(msg) => showToast(msg, 'error')}
        config={UPLOAD_CONFIGS.MATERIAL_LOG}
      />

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <MaterialAnalyticsDashboard
          data={materials}
          onClose={() => setShowAnalytics(false)}
        />
      )}

    </div>
  );
};

export default MaterialLog;
